import { useAuth } from 'common-react/auth/AuthProvider';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router';

const LOGIN_STUCK_TIMEOUT_MS = 30_000;

export const LoginGuard: React.FC<
  PropsWithChildren<{
    loadingComponent: React.ReactNode;
    onLoginTimeout: () => void;
  }>
> = ({ children, loadingComponent, onLoginTimeout }) => {
  const { authStoreAccess } = useAuth();
  const authenticationScheme = authStoreAccess.useAuthenticationScheme();
  const jwtToken = authStoreAccess.useJwtToken();

  const loginStatus = useMemo(
    () =>
      authenticationScheme && jwtToken
        ? 'loggedIn'
        : authenticationScheme
          ? 'loggingIn'
          : 'loggedOut',
    [authenticationScheme, jwtToken],
  );

  useEffect(() => {
    if (loginStatus !== 'loggingIn') {
      return;
    }

    const timeout = setTimeout(() => {
      onLoginTimeout();
    }, LOGIN_STUCK_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [loginStatus, onLoginTimeout]);

  if (loginStatus === 'loggedIn') {
    return <Navigate to="/" replace={true} />;
  }

  if (loginStatus === 'loggingIn') {
    return loadingComponent;
  }

  if (loginStatus === 'loggedOut') {
    return children;
  }

  loginStatus satisfies never;
};
